import { IonContent, IonPage } from '@ionic/react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';

import './auth.css';
import { setUserState } from '../redux/actions';
import { auth, createRefreshToken, writeBasicUserInfo, readUser } from '../firebase';
import {
  BrandPanel,
  Field,
  TextInput,
  PasswordInput,
  ArrowIcon,
  AlertIcon,
  isValidEmail,
} from './authShared';

const Login: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const partnerCode = new URLSearchParams(location.search).get('partner');
  // URL Example: https://app.rolloverrep.com/Login?redirect_uri=https://ctm.com
  const redirectUri = new URLSearchParams(location.search).get('redirect_uri');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [isOauth, setIsOauth] = useState(false);
  const [partner, setPartner] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (redirectUri) setIsOauth(true);
    if (partnerCode === 'ctm') setPartner('CTM');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loginUser(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy) return;
    if (!isValidEmail(email) || !password) {
      setError('Enter your work email and password.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      // "Keep me signed in" → persistent (local) vs session-only persistence.
      try {
        await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      } catch {
        /* persistence not supported here — proceed anyway */
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      dispatch(setUserState(user));
      await writeBasicUserInfo(user.uid, email);

      if (redirectUri) {
        const accessToken: any = await user.getIdToken();
        let refreshToken = '';
        const userInfo: any = await readUser(user.uid);
        if (userInfo && userInfo.private && userInfo.private.refresh_token) {
          refreshToken = userInfo.private.refresh_token;
        } else {
          refreshToken = await createRefreshToken(user.uid);
        }
        const encodedRedirectUri = encodeURIComponent(redirectUri);
        const encodedAccessToken = encodeURIComponent(accessToken);
        const encodedRefreshToken = encodeURIComponent(refreshToken);

        switch (partner) {
          case 'CTM':
            window.location.href = `/Integrations/CTM?redirect_uri=${encodedRedirectUri}&access_token=${encodedAccessToken}&refresh_token=${encodedRefreshToken}`;
            break;
          default:
            // Role-aware root redirect (App.tsx) takes over.
            window.location.href = '/';
            break;
        }
      } else {
        // Land on "/" so App.tsx's role-aware root redirect decides the
        // destination (operator → /Projects, recordings agent → /Recordings…).
        window.location.href = '/';
      }
    } catch (err: any) {
      // Don't leak which part failed; one friendly message covers the common
      // auth/* codes (wrong-password, user-not-found, invalid-credential).
      setError('That email or password doesn’t look right. Please try again.');
      // eslint-disable-next-line no-console
      console.log(err?.code, err?.message);
      setBusy(false);
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen className="rr-auth-content">
        <div className="auth">
          <BrandPanel
            eyebrow="welcome back"
            title={<>Every missed call can be <em>a help-seeker</em> who may not call back.</>}
            quote="Rollover Rep has proven to be an exceptional partner during a time of increased volume for our organization. I have been massively impressed by the effort they've put into missed-call management — I'm very pleased with this partnership, and their ongoing support of us and our patients."
            cite="— Josh, former COO, Guardian Health Network"
            stats={[
              { num: '30+', label: 'admissions per day' },
              { num: '100+', label: 'treatment facilities' },
              { num: '24/7/365', label: 'human + AI coverage' },
            ]}
          />

          <main className="auth-form">
            <div className="auth-form-top">
              <span>New to Rollover Rep?</span>
              {isOauth ? (
                <a href="https://app.usemotion.com/meet/rollover-rep-tyler/ctm">Create an account</a>
              ) : (
                <Link to="/Signup">Create an account</Link>
              )}
            </div>

            <div className="auth-form-body">
              <header className="auth-heading">
                <div className="auth-eyebrow">Sign in</div>
                <h2>Welcome back.</h2>
                <p>Sign in to your Rollover Rep account to pick up where you left off.</p>
              </header>

              {error && (
                <div className="auth-banner auth-banner--error">
                  <AlertIcon />
                  <span>{error}</span>
                </div>
              )}

              <form className="auth-fields" onSubmit={loginUser}>
                <Field label="Work email">
                  <TextInput type="email" value={email} onChange={setEmail} placeholder="you@facility.com" autoComplete="email" />
                </Field>

                <Field label="Password">
                  <PasswordInput value={password} onChange={setPassword} autoComplete="current-password" />
                </Field>

                <div className="auth-meta">
                  <label className="auth-check">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    <span className="auth-check-box" />
                    Keep me signed in on this device
                  </label>
                  <Link className="auth-forgot" to="/Reset">Forgot password?</Link>
                </div>
                {/* The checkbox controls Firebase persistence (does the session
                    survive closing the browser) — NOT the idle timeout, which is
                    a HIPAA automatic-logoff control and always applies. Say so
                    here so nobody expects it to prevent inactivity sign-outs. */}
                <p className="auth-hint">
                  You’ll still be signed out after 15 minutes of inactivity to protect patient
                  information — we’ll warn you first.
                </p>

                <button type="submit" className="auth-submit" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                  {!busy && <ArrowIcon />}
                </button>
              </form>
            </div>

            <div className="auth-form-foot">
              Trouble signing in?{' '}
              <a href="mailto:support@rolloverrep.com" style={{ color: 'var(--rr-red)', textDecoration: 'none', fontWeight: 600 }}>
                Contact support
              </a>
            </div>
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Login;
